/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module mention/mentionui
 */
import { Plugin } from 'ckeditor5/src/core.js';
import { CKEditorError, Collection, env, keyCodes, logWarning } from 'ckeditor5/src/utils.js';
import { TextWatcher } from 'ckeditor5/src/typing.js';
import { debounce } from 'lodash-es';
const VERTICAL_SPACING = 3;
// The key codes that mention UI handles when it is open (without commit keys).
const defaultHandledKeyCodes = [keyCodes.arrowup, keyCodes.arrowdown, keyCodes.esc];
// Dropdown commit key codes.
const defaultCommitKeyCodes = [keyCodes.enter, keyCodes.tab];
/**
 * The mention UI feature.
 */
export default class MentionUI extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return 'MentionUI';
  }
  /**
   * @inheritDoc
   */
  // public static get requires() {
  // 	return [ContextualBalloon] as const;
  // }
  /**
   * @inheritDoc
   */
  constructor(editor) {
    super(editor);
    /**
     * The contextual balloon plugin instance.
     */
    this._items = new Collection();
    // this._mentionsView = this._createMentionView();
    this._mentionsConfigurations = new Map();
    this._requestFeedDebounced = debounce(this._requestFeed, 100);
    this._debouncedRemoveList = debounce(this._removeList, 120);
    this._lastRenderTime = 0;

    editor.config.define('mention', { feeds: [] });
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const commitKeys = editor.config.get('mention.commitKeys') || defaultCommitKeyCodes;
    const handledKeyCodes = defaultHandledKeyCodes.concat(commitKeys);

    const feeds = editor.config.get('mention.feeds');
    for (const mentionDescription of feeds) {
      const { feed, marker, dropdownLimit } = mentionDescription;
      if (!isValidMentionMarker(marker)) {
        /**
         * The marker must be a single character.
         *
         * Correct markers: `'@'`, `'#'`.
         *
         * Incorrect markers: `'$$'`, `'[@'`.
         *
         * See {@link module:mention/mentionconfig~MentionConfig}.
         *
         * @error mentionconfig-incorrect-marker
         * @param marker Configured marker
         */
        throw new CKEditorError('mentionconfig-incorrect-marker', null, { marker });
      }
      const feedCallback = typeof feed == 'function' ? feed.bind(this.editor) : createFeedCallback(feed);
      const itemRenderer = mentionDescription.itemRenderer;
      const definition = {
        marker,
        feedCallback,
        itemRenderer,
        dropdownLimit,
      };
      this._mentionsConfigurations.set(marker, definition);
    }
    this._setupTextWatcher(feeds);

    this.on('requestFeed:response', (evt, data) => this._handleFeedResponse(data));

    /**
     * Checks if a given key code is handled by the mention UI.
     */
    function isHandledKey(keyCode) {
      return handledKeyCodes.includes(keyCode);
    }
    this.editor.model.document.on('mention:selectItem', (evt, data) => {
      const selectedItem = data.item;
      const mentionMarker = this.editor.model.markers.get('mention');
      if (mentionMarker) {
        this._handleItemSelected(selectedItem, mentionMarker);
      }
    });
  }

  _handleItemSelected(selectedItem) {
    if (!this._lastRange || !this._lastMarker) {
      console.error('Last range or marker not found');
      return;
    }

    const editor = this.editor;
    const model = editor.model;
    model.change((writer) => {
      // Restore the selection to where it was when the mention list was rendered
      writer.setSelection(this._lastRange);

      const end = model.createPositionAt(this._lastRange.end);
      const start = model.createPositionAt(this._lastMarker.getStart());
      const range = model.createRange(start, end);
      this.editor.model.change((writer) => writer.removeMarker('mention'));
      // Clear the preserved data

      this._lastRange = null;
      this._lastMarker = null;
      editor.execute('mention', {
        mention: selectedItem,
        text: selectedItem.text,
        marker: '@',
        range,
      });
    });

    // Ensure the editor regains focus
    editor.editing.view.focus();
  }
  /**
   * @inheritDoc
   */
  destroy() {
    super.destroy();
    // Destroy created UI components as they are not automatically destroyed (see ckeditor5#1341).
    this._mentionsView.destroy();
  }

  /**
   * Requests a feed from a configured callbacks.
   */
  _requestFeed(marker, feedText) {
    // Store the last requested feed - it is used to discard any out-of order requests.
    this._lastRequested = feedText;
    const { feedCallback } = this._mentionsConfigurations.get(marker);
    const feedResponse = feedCallback(feedText);
    const isAsynchronous = feedResponse instanceof Promise;
    // For synchronous feeds (e.g. callbacks, arrays) fire the response event immediately.
    if (!isAsynchronous) {
      this.fire('requestFeed:response', {
        feed: feedResponse,
        marker,
        feedText,
      });
      return;
    }
    // Handle the asynchronous responses.
    feedResponse
      .then((response) => {
        // Check the feed text of this response with the last requested one so either:
        if (this._lastRequested == feedText) {
          // It is the same and fire the response event.
          this.fire('requestFeed:response', { feed: response, marker, feedText });
        } else {
          // It is different - most probably out-of-order one, so fire the discarded event.
          this.fire('requestFeed:discarded', { feed: response, marker, feedText });
        }
      })
      .catch((error) => {
        this.fire('requestFeed:error', {
          error,
        });
        /**
         * The callback used for obtaining mention autocomplete feed thrown and error and the mention UI was hidden or
         * not displayed at all.
         *
         * @error mention-feed-callback-error
         */
        logWarning('mention-feed-callback-error', { marker });
      });
  }
  /**
   * Registers a text watcher for the marker.
   */
  _setupTextWatcher(feeds) {
    const editor = this.editor;
    const feedsWithPattern = feeds.map((feed) =>
      Object.assign(Object.assign({}, feed), { pattern: createRegExp(feed.marker, feed.minimumCharacters || 0) })
    );
    const watcher = new TextWatcher(editor.model, createTestCallback(feedsWithPattern));
    watcher.on('matched', (evt, data) => {
      const markerDefinition = getLastValidMarkerInText(feedsWithPattern, data.text);
      const selection = editor.model.document.selection;
      const focus = selection.focus;
      const markerPosition = editor.model.createPositionAt(focus.parent, markerDefinition.position);
      if (isPositionInExistingMention(focus) || isMarkerInExistingMention(markerPosition)) {
        this._hideUIAndRemoveMarker();
        return;
      }
      const feedText = requestFeedText(markerDefinition, data.text);
      const matchedTextLength = markerDefinition.marker.length + feedText.length;
      // Create a marker range.
      const start = focus.getShiftedBy(-matchedTextLength);
      const end = focus.getShiftedBy(-feedText.length);
      const markerRange = editor.model.createRange(start, end);
      if (checkIfStillInCompletionMode(editor)) {
        const mentionMarker = editor.model.markers.get('mention');
        // Update the marker - user might've moved the selection to other mention trigger.
        editor.model.change((writer) => {
          writer.updateMarker(mentionMarker, { range: markerRange });
        });
      } else {
        editor.model.change((writer) => {
          writer.addMarker('mention', {
            range: markerRange,
            usingOperation: false,
            affectsData: false,
          });
        });
      }
      this._requestFeedDebounced(markerDefinition.marker, feedText);
      this._lastRenderTime = Date.now();
    });
    watcher.on('unmatched', () => {
      this._debouncedRemoveList();
    });
    const mentionCommand = editor.commands.get('mention');
    watcher.bind('isEnabled').to(mentionCommand);
    return watcher;
  }

  _removeList() {
    const timeSinceLastRender = Date.now() - this._lastRenderTime;
    if (timeSinceLastRender < 300) {
      // If a render happened very recently, delay the removal a bit more
      setTimeout(() => this._removeList(), 300);
    } else {
      this.editor.model.document.fire('mention:removeList', {
        items: [],
      });
      this._hideUIAndRemoveMarker();
    }
  }
  /**
   * Handles the feed response event data.
   */
  _handleFeedResponse(data) {
    const { feed, marker } = data;
    // eslint-disable-next-line max-len
    // If the marker is not in the document happens when the selection had changed and the 'mention' marker was removed.
    if (!checkIfStillInCompletionMode(this.editor)) {
      return;
    }
    // Reset the view.
    this._items.clear();
    for (const feedItem of feed) {
      const item = typeof feedItem != 'object' ? { id: feedItem, text: feedItem } : feedItem;
      this._items.add({ item, marker });
    }
    this._lastRenderTime = Date.now();
    this._showOrUpdateUI();
  }
  /**
   * Shows the mentions balloon. If the panel is already visible, it will reposition it.
   */
  _showOrUpdateUI() {
    const mentionListItems = this._items.map((item) => item.item);
    const selection = this.editor.model.document.selection;
    this._lastRange = selection.getFirstRange();
    this._lastMarker = this.editor.model.markers.get('mention');
    this.editor.model.document.fire('mention:renderList', {
      items: mentionListItems,
    });
  }
  /**
   * Hides the mentions balloon and removes the 'mention' marker from the markers collection.
   */
  _hideUIAndRemoveMarker() {}
}

/**
 * Returns a marker definition of the last valid occurring marker in a given string.
 * If there is no valid marker in a string, it returns undefined.
 *
 * Example of returned object:
 *
 * ```ts
 * {
 * 	marker: '@',
 * 	position: 4,
 * 	minimumCharacters: 0
 * }
 * ````
 *
 * @param feedsWithPattern Registered feeds in editor for mention plugin with created RegExp for matching marker.
 * @param text String to find the marker in
 * @returns Matched marker's definition
 */
function getLastValidMarkerInText(feedsWithPattern, text) {
  let lastValidMarker;
  for (const feed of feedsWithPattern) {
    const currentMarkerLastIndex = text.lastIndexOf(feed.marker);
    if (currentMarkerLastIndex > 0 && !text.substring(currentMarkerLastIndex - 1).match(feed.pattern)) {
      continue;
    }
    if (!lastValidMarker || currentMarkerLastIndex >= lastValidMarker.position) {
      lastValidMarker = {
        marker: feed.marker,
        position: currentMarkerLastIndex,
        minimumCharacters: feed.minimumCharacters,
        pattern: feed.pattern,
      };
    }
  }
  return lastValidMarker;
}
/**
 * Creates a RegExp pattern for the marker.
 *
 * Function has to be exported to achieve 100% code coverage.
 */
export function createRegExp(marker, minimumCharacters) {
  const numberOfCharacters = minimumCharacters == 0 ? '*' : `{${minimumCharacters},}`;
  const openAfterCharacters = env.features.isRegExpUnicodePropertySupported ? '\\p{Ps}\\p{Pi}"\'' : '\\(\\[{"\'';
  const mentionCharacters = '.';
  // The pattern consists of 3 groups:
  // - 0 (non-capturing): Opening sequence - start of the line, space or an opening punctuation character like "(" or "\"",
  // - 1: The marker character,
  // - 2: Mention input (taking the minimal length into consideration to trigger the UI),
  //
  // The pattern matches up to the caret (end of string switch - $).
  //               (0:      opening sequence       )(1:   marker  )(2:                typed mention              )$
  const pattern = `(?:^|[ ${openAfterCharacters}])([${marker}])(${mentionCharacters}${numberOfCharacters})$`;
  return new RegExp(pattern, 'u');
}
/**
 * Creates a test callback for the marker to be used in the text watcher instance.
 *
 * @param feedsWithPattern Feeds of mention plugin configured in editor with RegExp to match marker in text
 */
function createTestCallback(feedsWithPattern) {
  const textMatcher = (text) => {
    const markerDefinition = getLastValidMarkerInText(feedsWithPattern, text);
    if (!markerDefinition) {
      return false;
    }
    let splitStringFrom = 0;
    if (markerDefinition.position !== 0) {
      splitStringFrom = markerDefinition.position - 1;
    }
    const textToTest = text.substring(splitStringFrom);
    return markerDefinition.pattern.test(textToTest);
  };
  return textMatcher;
}
/**
 * Creates a text matcher from the marker.
 */
function requestFeedText(markerDefinition, text) {
  let splitStringFrom = 0;
  if (markerDefinition.position !== 0) {
    splitStringFrom = markerDefinition.position - 1;
  }
  const regExp = createRegExp(markerDefinition.marker, 0);
  const textToMatch = text.substring(splitStringFrom);
  const match = textToMatch.match(regExp);
  return match[2];
}
/**
 * The default feed callback.
 */
function createFeedCallback(feedItems) {
  return (feedText) => {
    const filteredItems = feedItems
      // Make the default mention feed case-insensitive.
      .filter((item) => {
        // Item might be defined as object.
        const itemId = typeof item == 'string' ? item : String(item.id);
        // The default feed is case insensitive.
        return itemId.toLowerCase().includes(feedText.toLowerCase());
      });
    return filteredItems;
  };
}
/**
 * Checks if position in inside or right after a text with a mention.
 */
function isPositionInExistingMention(position) {
  // The text watcher listens only to changed range in selection - so the selection attributes are not yet available
  // and you cannot use selection.hasAttribute( 'mention' ) just yet.
  // See https://github.com/ckeditor/ckeditor5-engine/issues/1723.
  const hasMention = position.textNode && position.textNode.hasAttribute('mention');
  const nodeBefore = position.nodeBefore;
  return hasMention || (nodeBefore && nodeBefore.is('$text') && nodeBefore.hasAttribute('mention'));
}
/**
 * Checks if the closest marker offset is at the beginning of a mention.
 *
 * See https://github.com/ckeditor/ckeditor5/issues/11400.
 */
function isMarkerInExistingMention(markerPosition) {
  const nodeAfter = markerPosition.nodeAfter;
  return nodeAfter && nodeAfter.is('$text') && nodeAfter.hasAttribute('mention');
}
/**
 * Checks if string is a valid mention marker.
 */
function isValidMentionMarker(marker) {
  return marker && marker.length == 1;
}
/**
 * Checks the mention plugins is in completion mode (e.g. when typing is after a valid mention string like @foo).
 */
function checkIfStillInCompletionMode(editor) {
  return editor.model.markers.has('mention');
}
